import { Suspense, useEffect } from 'react'
import { useLocation, Navigate, Outlet } from 'react-router-dom'
import { useReapitConnect } from '@reapit/connect-session'
import { reapitConnectBrowserSession } from '@reapit-sales/reapit-connect'
import { Loader, MainContainer, PageContainer } from '@reapit/elements'
import { UserConfigurationsProvider } from '@reapit-sales/api-v2'
import { OrganisationsProvider } from '@reapit-sales/api-v3'
import { LegacyCRMIframe } from '@reapit-sales/legacy-crm'
import { LoadLiveSupportChat, UserIdentityWrapper } from '@reapit-sales/utilities'
import { buildLitmosRedirectPageURL } from '@crm/components/litmos/buildLitmosRedirectPageURL'
import { UnauthorizedUserWrapper } from '@crm/components/error-pages/UnauthorizedUserWrapper'
import Nav from './navigation'
import { useHandleLogout } from '@crm/hooks'
import { LAST_LOGOUT_TIMESTAMP_KEY } from '@crm/constants'
import { RoutesPrivilege } from './routes-privilege'

export const PrivateRouteWrapper = () => {
  const { connectSession, connectInternalRedirect } = useReapitConnect(reapitConnectBrowserSession)
  const handleLogout = useHandleLogout()
  const location = useLocation()

  // Below useEffect is used to handle logout when the 'lastLogoutTimestamp' is set in localStorage.
  // This is to ensure that if the user logs out from another tab, the current tab also reflects the logout state.
  // It listens for the 'storage' event and calls handleLogout when the 'lastLogoutTimestamp' key is changed.
  // This is particularly useful in multi-tab scenarios where the user might log out from one tab,
  // and we want to ensure all tabs are updated accordingly by logging out the user in the rest of the tabs as well.
  useEffect(() => {
    if (connectSession) {
      const onStorageChange = (event: StorageEvent) => {
        if (event.key === LAST_LOGOUT_TIMESTAMP_KEY) {
          handleLogout()
        }
      }
      window.addEventListener('storage', onStorageChange)
      return () => window.removeEventListener('storage', onStorageChange)
    }
  }, [connectSession, handleLogout])

  if (!connectSession) {
    return (
      <MainContainer hasGreyBackground>
        <PageContainer>
          <Loader fullPage />
        </PageContainer>
      </MainContainer>
    )
  }

  /**
   * Check if the current page is the Litmos redirect page
   * This is to avoid rendering the Nav and LegacyCRMIframe on the Litmos redirect
   * as it is handled by the LitmosRedirect component
   * This is a workaround until we can handle the Litmos redirect in a more elegant way
   */
  if (location.pathname === buildLitmosRedirectPageURL()) {
    return (
      <MainContainer hasGreyBackground>
        <Suspense fallback={<Loader fullPage />}>
          <Outlet />
        </Suspense>
      </MainContainer>
    )
  }

  if (connectInternalRedirect && location?.pathname !== connectInternalRedirect) {
    return <Navigate to={connectInternalRedirect} replace />
  }

  return (
    <UnauthorizedUserWrapper>
      <OrganisationsProvider>
        <UserConfigurationsProvider>
          <UserIdentityWrapper>
            <MainContainer hasGreyBackground>
              <Nav />
              <Suspense fallback={<Loader fullPage />}>
                <RoutesPrivilege>
                  <LegacyCRMIframe />
                </RoutesPrivilege>
              </Suspense>
              {/*
            // CM-274 We will host Dashboard and Sales Trust as it's own iframe
            // Once the communication between all iframes are resolved. 
            <LegacyDashboardIframe />
            <LegacyTrustIframe /> 
            */}
              <LoadLiveSupportChat />
            </MainContainer>
          </UserIdentityWrapper>
        </UserConfigurationsProvider>
      </OrganisationsProvider>
    </UnauthorizedUserWrapper>
  )
}

export default PrivateRouteWrapper
